import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  startClearCompetitorFavoriteDestination,
  startCreateCompetitorFolder,
  startListenerCompetitorFavoriteDestination,
  startListenerCompetitorFolderContents,
  startListenerCompetitorFolders,
  startMoveCompetitorAdsToFolder,
  startRenameCompetitorFolder,
  startSetCompetitorFavoriteDestination,
  startUnassignCompetitorAdsFromFolder,
  stopListenerCompetitorFavoriteDestination,
  stopListenerCompetitorFolderContents,
  stopListenerCompetitorFolders,
  startDeleteCompetitorFolder,
} from "../actions/competitorFolders";
import { SimpleAlert } from "../components/Alerts/Alerts";
import { useTranslationApp } from "../lib/i18next";
import { useDispatchApp } from "../lib/redux";
import { useConfirmDeleteFolder } from "./useConfirmDeleteFolder";
import {
  ERROR,
  GALLERY_FOLDERS_MOVE_ERROR,
  GALLERY_FOLDERS_MOVE_SUCCESS,
  GALLERY_FOLDERS_UNASSIGN_ERROR,
  GALLERY_FOLDERS_UNASSIGN_SUCCESS,
} from "../i18n/keysTranslations";
import { ALL_FILTER, CONTENT_FOLDER_ROOT_ID } from "../utils/constants";
import {
  getFolderPathLabel,
  isCompetitorFavoritesFolder,
} from "../utils/contentFolderUtils";

export const useCompetitorsFolderSession = ({
  modalOpen,
  folderFilterID,
  onFolderFilterChange,
  clearSelection,
}) => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const [foldersByID, setFoldersByID] = useState({});
  const [folderMembershipByContentID, setFolderMembershipByContentID] =
    useState({});
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [isMovingContents, setIsMovingContents] = useState(false);
  const [isRenamingFolder, setIsRenamingFolder] = useState(false);
  const [isOpenCreateFolderModal, setIsOpenCreateFolderModal] = useState(false);
  const [isOpenMoveFolderModal, setIsOpenMoveFolderModal] = useState(false);
  const [isOpenRenameFolderModal, setIsOpenRenameFolderModal] = useState(false);
  const [renameFolderTarget, setRenameFolderTarget] = useState(null);
  const [favoriteDestinationFolderID, setFavoriteDestinationFolderID] =
    useState(null);
  const foldersListenerRef = useRef(null);
  const contentsListenerRef = useRef(null);
  const favoriteDestinationListenerRef = useRef(null);

  const activeFolderID = folderFilterID === ALL_FILTER ? null : folderFilterID;
  const realParentFolderID = isCompetitorFavoritesFolder(activeFolderID)
    ? null
    : activeFolderID;

  useEffect(() => {
    if (!modalOpen) {
      setFoldersByID({});
      setFolderMembershipByContentID({});
      setFavoriteDestinationFolderID(null);
      setIsLoadingFolders(false);
      return undefined;
    }

    setIsLoadingFolders(true);

    dispatch(
      startListenerCompetitorFolders({
        onUpdatedDataCallback: (folders) => {
          setFoldersByID(folders || {});
          setIsLoadingFolders(false);
        },
        onReferenceAvailable: (dbRef) => {
          foldersListenerRef.current = dbRef;
        },
      })
    ).then((started) => {
      if (!started) setIsLoadingFolders(false);
    });

    dispatch(
      startListenerCompetitorFolderContents({
        onUpdatedDataCallback: (membership) => {
          setFolderMembershipByContentID(membership || {});
        },
        onReferenceAvailable: (dbRef) => {
          contentsListenerRef.current = dbRef;
        },
      })
    );

    dispatch(
      startListenerCompetitorFavoriteDestination({
        onUpdatedDataCallback: setFavoriteDestinationFolderID,
        onReferenceAvailable: (dbRef) => {
          favoriteDestinationListenerRef.current = dbRef;
        },
      })
    );

    return () => {
      dispatch(
        stopListenerCompetitorFolders({
          listenerRef: foldersListenerRef.current,
        })
      );
      dispatch(
        stopListenerCompetitorFolderContents({
          listenerRef: contentsListenerRef.current,
        })
      );
      dispatch(
        stopListenerCompetitorFavoriteDestination({
          listenerRef: favoriteDestinationListenerRef.current,
        })
      );
      foldersListenerRef.current = null;
      contentsListenerRef.current = null;
      favoriteDestinationListenerRef.current = null;
    };
  }, [dispatch, modalOpen]);

  const folders = useMemo(() => {
    return Object.entries(foldersByID)
      .map(([id, folder]) => ({
        id,
        ...folder,
      }))
      .sort((left, right) => right.creationTime - left.creationTime);
  }, [foldersByID]);

  const getFolderLabel = useCallback(
    ({ folderID }) => getFolderPathLabel({ folders, folderID }),
    [folders]
  );

  const onCreateFolder = useCallback(
    async ({ name, parentFolderID }) => {
      setIsCreatingFolder(true);
      try {
        return await dispatch(
          startCreateCompetitorFolder({ name, parentFolderID })
        );
      } finally {
        setIsCreatingFolder(false);
      }
    },
    [dispatch]
  );

  const applyMoveContents = useCallback(
    async ({ contentIDs, folderID }) => {
      if (isCompetitorFavoritesFolder(folderID)) {
        return { ok: false, reason: "noChanges" };
      }

      if (!contentIDs?.length) {
        return { ok: false, reason: "noChanges" };
      }

      setIsMovingContents(true);
      try {
        const response = folderID
          ? await dispatch(
              startMoveCompetitorAdsToFolder({
                contentIDs,
                folderID,
                folderMembershipByContentID,
              })
            )
          : await dispatch(
              startUnassignCompetitorAdsFromFolder({
                contentIDs,
                folderMembershipByContentID,
              })
            );

        return response;
      } finally {
        setIsMovingContents(false);
      }
    },
    [dispatch, folderMembershipByContentID]
  );

  const onDropMany = useCallback(
    async ({ contentIDs, folderID }) => {
      if (isCompetitorFavoritesFolder(folderID)) return;
      if (!contentIDs?.length) return;

      const response = await applyMoveContents({ contentIDs, folderID });

      if (!response?.ok) {
        if (response?.reason === "noChanges") return;

        await SimpleAlert({
          icon: "error",
          title: t(ERROR),
          text: t(
            folderID
              ? GALLERY_FOLDERS_MOVE_ERROR
              : GALLERY_FOLDERS_UNASSIGN_ERROR
          ),
        });
        return;
      }

      await SimpleAlert({
        icon: "success",
        title: t(
          folderID
            ? GALLERY_FOLDERS_MOVE_SUCCESS
            : GALLERY_FOLDERS_UNASSIGN_SUCCESS
        ),
      });
    },
    [applyMoveContents, t]
  );

  const onRenameFolder = useCallback(
    async ({ folderID, name }) => {
      if (isCompetitorFavoritesFolder(folderID)) return { ok: false };

      setIsRenamingFolder(true);
      try {
        return await dispatch(startRenameCompetitorFolder({ folderID, name }));
      } finally {
        setIsRenamingFolder(false);
      }
    },
    [dispatch]
  );

  const setFavoriteDestination = useCallback(
    async ({ folderID }) =>
      dispatch(startSetCompetitorFavoriteDestination({ folderID })),
    [dispatch]
  );

  const clearFavoriteDestination = useCallback(
    async () => dispatch(startClearCompetitorFavoriteDestination()),
    [dispatch]
  );

  const onSelectFolder = useCallback(
    (folderID) => {
      clearSelection();
      onFolderFilterChange(folderID === null ? ALL_FILTER : folderID);
    },
    [clearSelection, onFolderFilterChange]
  );

  const onCreateFolderInContext = useCallback(
    async ({ name, parentFolderID }) =>
      onCreateFolder({
        name,
        parentFolderID: parentFolderID ?? realParentFolderID,
      }),
    [onCreateFolder, realParentFolderID]
  );

  const openCreateFolderModal = useCallback(() => {
    setIsOpenCreateFolderModal(true);
  }, []);

  const closeCreateFolderModal = useCallback(() => {
    setIsOpenCreateFolderModal(false);
  }, []);

  const openMoveFolderModal = useCallback(() => {
    setIsOpenMoveFolderModal(true);
  }, []);

  const closeMoveFolderModal = useCallback(() => {
    setIsOpenMoveFolderModal(false);
  }, []);

  const openRenameFolderModal = useCallback((folder) => {
    if (folder?.isSystem || isCompetitorFavoritesFolder(folder?.id)) return;

    setRenameFolderTarget(folder);
    setIsOpenRenameFolderModal(true);
  }, []);

  const closeRenameFolderModal = useCallback(() => {
    setIsOpenRenameFolderModal(false);
    setRenameFolderTarget(null);
  }, []);

  const createFolderParentID = realParentFolderID ?? CONTENT_FOLDER_ROOT_ID;
  const createFolderParentLabel = realParentFolderID
    ? getFolderLabel({ folderID: realParentFolderID })
    : null;

  const deleteFolder = useCallback(
    async ({ folderID }) => {
      if (isCompetitorFavoritesFolder(folderID)) return { ok: false };

      return dispatch(startDeleteCompetitorFolder({ folderID }));
    },
    [dispatch]
  );

  const onFolderDeleted = useCallback(
    ({ folder }) => {
      if (activeFolderID === folder.id) onSelectFolder(null);
      if (favoriteDestinationFolderID === folder.id) {
        clearFavoriteDestination();
      }
    },
    [
      activeFolderID,
      onSelectFolder,
      favoriteDestinationFolderID,
      clearFavoriteDestination,
    ]
  );

  const onDeleteFolder = useConfirmDeleteFolder({
    deleteFolder,
    onDeleted: onFolderDeleted,
  });

  return {
    folders,
    foldersByID,
    activeFolderID,
    folderMembershipByContentID,
    favoriteDestinationFolderID,
    isLoadingFolders,
    isCreatingFolder,
    isMovingContents,
    isRenamingFolder,
    onSelectFolder,
    onDropMany,
    moveContents: applyMoveContents,
    setFavoriteDestination,
    clearFavoriteDestination,
    isOpenCreateFolderModal,
    openCreateFolderModal,
    closeCreateFolderModal,
    isOpenMoveFolderModal,
    openMoveFolderModal,
    closeMoveFolderModal,
    onCreateFolderInContext,
    createFolderParentID,
    createFolderParentLabel,
    getFolderLabel,
    isOpenRenameFolderModal,
    openRenameFolderModal,
    closeRenameFolderModal,
    renameFolderTarget,
    onRenameFolder,
    onDeleteFolder,
  };
};

export default useCompetitorsFolderSession;
